import { decodeJwt } from "@/auth/jwt";
import { tokenStore } from "@/auth/tokenStore";

/**
 * localStorage scoped to the signed-in user, so preferences don't leak
 * between accounts sharing a browser profile. Keys become
 * `3t-bridge:<user-sub>:<key>`; before login the scope is "anon".
 *
 * Reads fall back to the legacy unscoped `3t-bridge:<key>` so settings
 * saved before per-user scoping still load once; writes are always scoped.
 */

function currentUserId(): string {
  try {
    const jwt = tokenStore.getIdToken() ?? tokenStore.getAccessToken();
    if (!jwt) return "anon";
    return decodeJwt(jwt)?.sub ?? "anon";
  } catch {
    return "anon";
  }
}

function scopedKey(key: string): string {
  return `3t-bridge:${currentUserId()}:${key}`;
}

export const userStorage = {
  getItem(key: string): string | null {
    try {
      return (
        localStorage.getItem(scopedKey(key)) ??
        localStorage.getItem(`3t-bridge:${key}`)
      );
    } catch {
      return null;
    }
  },

  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(scopedKey(key), value);
    } catch {
      // Storage unavailable (private mode, quota) — value just won't
      // survive a refresh.
    }
  },

  removeItem(key: string): void {
    try {
      localStorage.removeItem(scopedKey(key));
    } catch {
      // Ignore — nothing to remove if storage is unavailable.
    }
  },
};

// Keys the app no longer reads, left in browsers by earlier builds. Each
// is removed on load, in the user's scope and (for the one that predates
// scoping) the raw key too. Entries can go once a release has shipped.
const RETIRED_KEYS = [
  // Chat renames kept locally until the engine had PATCH (TTTL-23);
  // since 2026-10-05 the engine is the only source and failures toast.
  { key: "ai-chat:title-overrides", raw: "ai-chat-title-overrides" },
  // The Agent sidebar width from the hand-rolled drag hook; the panel
  // split lives under react-resizable-panels' own key now (TTTL-237).
  { key: "agent:sidebar-width" },
];

/** Clear retired keys for the signed-in user. Called once the session
 *  is known, so the scope is the user's and not "anon". */
export function retireStoredKeys(): void {
  for (const { key, raw } of RETIRED_KEYS) {
    userStorage.removeItem(key);
    if (raw) {
      try {
        localStorage.removeItem(raw);
      } catch {
        // Storage unavailable — nothing to clear.
      }
    }
  }
}
